import { useRef, useEffect } from 'react'
import CreativeEditorSDK, { Configuration } from '@cesdk/cesdk-js'
import { mergeDeep } from 'remeda'

import { assetsAssetLibrary } from './assets/assets-asset-library'
import { productAssetsLibrary } from './assets/products-asset-library'
import { unsplashAssetLibrary } from './assets/unsplash-asset-library'
import { baseConfig } from './config/base.config'
import { dockOrder } from './config/dock.config'
import { templates } from './config/templates.config'
import { useImglyLicense } from './queries'

function useImgly({
  config = {},
  initialScene,
}: {
  config?: Configuration
  initialScene?: string | null
}) {
  const ref = useRef<HTMLDivElement | null>(null)
  const configRef = useRef(config)

  const license = useImglyLicense()

  useEffect(() => {
    if (!ref.current) return
    if (!license.data) return

    let cleanedUp = false
    let instance: CreativeEditorSDK

    CreativeEditorSDK.create(ref.current, {
      license: license.data.license,
      ...mergeDeep(baseConfig, configRef.current),
    }).then(async (_instance) => {
      instance = _instance
      if (cleanedUp) {
        instance.dispose()
        return
      }

      instance.feature.enable('ly.img.placeholder', false)
      instance.feature.enable('ly.img.page.add', false)
      instance.feature.enable('ly.img.preview', false)

      /* UNLAYER */
      instance.engine.asset.addSource(unsplashAssetLibrary)
      instance.ui.addAssetLibraryEntry({
        id: 'unsplash-entry',
        sourceIds: ['unsplash'],
        previewLength: 3,
        gridItemHeight: 'auto',
        gridBackgroundType: 'cover',
        gridColumns: 2,
        title: 'Stock Images',
      })

      // Use Unlayer Library for image replacement
      instance.ui.setReplaceAssetLibraryEntries(({ selectedBlocks }) => {
        if (
          selectedBlocks.length !== 1 ||
          selectedBlocks[0].fillType !== '//ly.img.ubq/fill/image'
        ) {
          return []
        }
        return ['products-entry', 'assets-entry', 'unsplash-entry']
      })

      /* SHOPWITH */
      instance.engine.asset.addSource(productAssetsLibrary)
      instance.engine.asset.addSource(assetsAssetLibrary)
      instance.ui.addAssetLibraryEntry({
        id: 'products-entry',
        sourceIds: ['products-assets'],
        previewLength: 3,
        gridItemHeight: 'auto',
        gridBackgroundType: 'cover',
        gridColumns: 2,
        title: 'Products',
      })
      instance.ui.addAssetLibraryEntry({
        id: 'assets-entry',
        sourceIds: ['assets-assets'],
        previewLength: 3,
        gridItemHeight: 'auto',
        gridBackgroundType: 'cover',
        gridColumns: 2,
        title: 'Assets',
      })

      /* ONESHOP TEMPLATES */
      instance.engine.asset.addLocalSource(
        'oneshop-templates',
        undefined,
        async (asset) => {
          if (!asset.meta?.uri) return
          instance.engine.scene.applyTemplateFromURL(asset.meta.uri)
          return undefined
        }
      )
      templates.forEach((template) =>
        instance.engine.asset.addAssetToSource('oneshop-templates', template)
      )
      instance.ui.addAssetLibraryEntry({
        id: 'oneshop-templates-entry',
        sourceIds: ['oneshop-templates'],
        sceneMode: 'Design',
        previewLength: 5,
        previewBackgroundType: 'cover',
        gridBackgroundType: 'cover',
        gridColumns: 3,
      })

      await Promise.all([
        instance.addDefaultAssetSources({
          baseURL: 'https://imgly.oneshopretail.com/assets',
        }),
        instance.addDemoAssetSources({ sceneMode: 'Design' }),
      ])

      instance.ui.setDockOrder(dockOrder)

      await instance.createDesignScene()

      if (initialScene) await instance.engine.scene.loadFromString(initialScene)
    })

    const cleanup = () => {
      cleanedUp = true
      instance?.dispose()
    }

    return cleanup
  }, [initialScene, license.data])

  return { ref }
}

export { useImgly }
